import { useState } from "react";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MapPin } from "lucide-react";
import { MultiClientPicker } from "./clients/MultiClientPicker";

const lbl = "text-[11px] font-mono uppercase tracking-[0.14em] text-[#475467]";

export function CreateGalleryProjectDialog({ onClose, onCreated }) {
  const [address, setAddress] = useState("");
  const [projectName, setProjectName] = useState("");
  const [clients, setClients] = useState([]);
  const [saving, setSaving] = useState(false);

  const create = async () => {
    const [primary, ...rest] = clients;
    if (!primary) return toast.error("Add at least one client for this project");
    setSaving(true);
    try {
      const response = await api.post("/admin/orders", {
        contact: { name: primary.name, email: primary.email, phone: primary.phone || "", company: primary.company || "" },
        address: { street: address.trim() },
        order_type: "Gallery / Delivery Project",
        gallery_only: true,
        save_as_client: false,
        existing_client_id: primary.id,
        additional_client_ids: rest.map((c) => c.id),
        project_name: projectName.trim() || null,
      });
      toast.success(`Project ${response.data.job_number} created`);
      onCreated(response.data);
      onClose();
    } catch (error) { toast.error(formatError(error.response?.data?.detail)); } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && !saving && onClose()}>
      <DialogContent data-testid="create-gallery-project-dialog" className="max-w-md rounded-2xl p-0 overflow-hidden">
        <DialogHeader className="px-7 pt-7 pb-1">
          <DialogTitle className="font-serif-display text-3xl font-normal text-[#101828]">Create New Project</DialogTitle>
          <DialogDescription className="text-sm text-[#475467]">Start a gallery delivery project — add property, pricing and scheduling details anytime from Orders.</DialogDescription>
        </DialogHeader>
        <div className="px-7 py-5 space-y-5">
          <div>
            <Label className={lbl}>Property address (optional)</Label>
            <div className="relative mt-1.5">
              <MapPin size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
              <Input data-testid="gallery-project-address" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="123 Main St, Houston, TX" className="pl-9 rounded-lg border-[#E4E9F0]" />
            </div>
          </div>
          <div>
            <Label className={lbl}>Project name</Label>
            <Input data-testid="gallery-project-name" value={projectName} onChange={(e) => setProjectName(e.target.value)} placeholder={address.trim() || "e.g. 123 Main St Listing"} className="mt-1.5 rounded-lg border-[#E4E9F0]" />
          </div>
          <div>
            <Label className={lbl}>Client(s)</Label>
            <div className="mt-1.5"><MultiClientPicker clients={clients} onChange={setClients} /></div>
          </div>
        </div>
        <div className="px-7 py-5 border-t border-[#E4E9F0] bg-[#F7F9FC] flex justify-end gap-2">
          <Button data-testid="gallery-project-cancel" variant="ghost" disabled={saving} onClick={onClose} className="rounded-xl">Cancel</Button>
          <Button data-testid="gallery-project-create" disabled={saving} onClick={create} className="rounded-xl bg-[#0C2347] hover:bg-[#123262] text-white px-6">{saving ? "Creating…" : "Create Project"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
